import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../../ui/card';
import { Button } from '../../ui/button';
import { CheckCircle, Info } from 'lucide-react';
import { EQ5D5L_CONFIG, validateEQ5D5LAnswers, generateHealthStateCode } from './eq5d5lSchema';
import VASScale from './VASScale';

/**
 * EQ5D5LQuestionnaire - Componente para el cuestionario EQ-5D-5L
 * 
 * Props:
 * - onComplete: callback con los resultados
 * - initialAnswers: respuestas previas (para edición)
 * - showVAS: mostrar escala VAS adicional
 * - config: configuración personalizada de textos
 */
const EQ5D5LQuestionnaire = ({ 
  onComplete, 
  initialAnswers = {}, 
  showVAS = true,
  config = EQ5D5L_CONFIG 
}) => {
  const [answers, setAnswers] = useState({
    mobility_level: initialAnswers.mobility_level || null,
    selfcare_level: initialAnswers.selfcare_level || null,
    usual_activities_level: initialAnswers.usual_activities_level || null,
    pain_discomfort_level: initialAnswers.pain_discomfort_level || null,
    anxiety_depression_level: initialAnswers.anxiety_depression_level || null,
    eq_vas_0_100: initialAnswers.eq_vas_0_100 ?? null,
  });
  
  const [errors, setErrors] = useState([]);
  const [touched, setTouched] = useState({});

  const dimensions = [
    { key: 'mobility_level', config: config.texts.dimensions.mobility },
    { key: 'selfcare_level', config: config.texts.dimensions.selfcare },
    { key: 'usual_activities_level', config: config.texts.dimensions.usual_activities },
    { key: 'pain_discomfort_level', config: config.texts.dimensions.pain_discomfort },
    { key: 'anxiety_depression_level', config: config.texts.dimensions.anxiety_depression },
  ];

  const handleDimensionChange = (dimensionKey, value) => {
    setAnswers(prev => ({ ...prev, [dimensionKey]: value }));
    setTouched(prev => ({ ...prev, [dimensionKey]: true }));
  };

  const handleVASChange = (value) => {
    setAnswers(prev => ({ ...prev, eq_vas_0_100: value }));
  };

  const handleSubmit = () => {
    const validation = validateEQ5D5LAnswers(answers);
    
    if (!validation.isValid) {
      setErrors(validation.errors);
      // Marcar todas como touched para mostrar errores
      const allTouched = {};
      dimensions.forEach(d => allTouched[d.key] = true);
      setTouched(allTouched);
      return;
    }
    
    setErrors([]);
    
    const healthStateCode = generateHealthStateCode(answers);
    
    onComplete({
      answers,
      health_state_code: healthStateCode,
      eq_vas_0_100: answers.eq_vas_0_100,
    });
  };

  // Calcular progreso
  const answeredCount = dimensions.filter(d => answers[d.key] !== null).length;
  const progress = (answeredCount / dimensions.length) * 100;

  // Health state code preview
  const previewCode = generateHealthStateCode(answers);

  return (
    <div className="space-y-6">
      {/* Cabecera con instrucciones */}
      <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
        <div className="flex items-start gap-3">
          <Info className="w-5 h-5 text-blue-600 mt-0.5 flex-shrink-0" />
          <div>
            <h3 className="font-medium text-blue-900">{config.texts.title}</h3>
            {config.texts.subtitle && (
              <p className="text-sm text-blue-700">{config.texts.subtitle}</p>
            )}
            <p className="text-sm text-blue-700 mt-1">{config.texts.instructions}</p>
          </div>
        </div>
      </div>

      {/* Barra de progreso */}
      <div className="space-y-2">
        <div className="flex justify-between text-sm">
          <span className="text-gray-600">Progreso</span>
          <span className="font-medium">{answeredCount} de {dimensions.length} dimensiones</span>
        </div>
        <div className="h-2 bg-gray-200 rounded-full overflow-hidden">
          <div 
            className="h-full bg-teal-500 transition-all duration-300"
            style={{ width: `${progress}%` }}
          />
        </div>
      </div>

      {/* Dimensiones */}
      <div className="space-y-4">
        {dimensions.map((dimension, idx) => {
          const isAnswered = answers[dimension.key] !== null;
          const showError = touched[dimension.key] && !isAnswered;
          
          return (
            <Card 
              key={dimension.key} 
              className={`border-2 transition-colors ${
                isAnswered ? 'border-teal-200 bg-teal-50/30' : 
                showError ? 'border-red-300 bg-red-50/30' : 
                'border-gray-200'
              }`}
            >
              <CardHeader className="pb-3">
                <CardTitle className="text-base flex items-center gap-2">
                  <span className={`w-6 h-6 rounded-full flex items-center justify-center text-sm font-bold ${
                    isAnswered ? 'bg-teal-500 text-white' : 'bg-gray-200 text-gray-600'
                  }`}>
                    {isAnswered ? <CheckCircle className="w-4 h-4" /> : idx + 1}
                  </span>
                  {dimension.config.title}
                </CardTitle>
                {dimension.config.question && (
                  <p className="text-sm text-gray-600 ml-8">{dimension.config.question}</p>
                )}
              </CardHeader>
              <CardContent className="pt-0">
                <div className="ml-8 space-y-2">
                  {dimension.config.levels.map((level) => (
                    <label 
                      key={level.value}
                      className={`flex items-center gap-3 p-3 rounded-lg border cursor-pointer transition-all ${
                        answers[dimension.key] === level.value
                          ? 'border-teal-500 bg-teal-50 ring-2 ring-teal-200'
                          : 'border-gray-200 hover:border-gray-300 hover:bg-gray-50'
                      }`}
                    >
                      <input
                        type="radio"
                        name={dimension.key}
                        value={level.value}
                        checked={answers[dimension.key] === level.value}
                        onChange={() => handleDimensionChange(dimension.key, level.value)}
                        className="w-4 h-4 text-teal-600 focus:ring-teal-500"
                      />
                      <span className={`flex-1 text-sm ${
                        answers[dimension.key] === level.value ? 'font-medium text-teal-900' : 'text-gray-700'
                      }`}>
                        {level.label}
                      </span>
                      <span className={`text-xs px-2 py-0.5 rounded ${
                        level.value === 1 ? 'bg-green-100 text-green-700' :
                        level.value === 2 ? 'bg-lime-100 text-lime-700' :
                        level.value === 3 ? 'bg-yellow-100 text-yellow-700' :
                        level.value === 4 ? 'bg-orange-100 text-orange-700' :
                        'bg-red-100 text-red-700'
                      }`}>
                        {level.value}
                      </span>
                    </label>
                  ))}
                </div>
                {showError && (
                  <p className="text-red-500 text-sm mt-2 ml-8">Esta dimensión es obligatoria</p>
                )}
              </CardContent>
            </Card>
          );
        })}
      </div>

      {/* EQ-VAS (opcional) */}
      {showVAS && config.enableVAS && (
        <Card className="border-2 border-gray-200">
          <CardHeader>
            <CardTitle className="text-base">{config.texts.vasLabel || 'Salud global hoy (0–100)'}</CardTitle>
            <p className="text-sm text-gray-600">{config.texts.vasDescription || config.texts.vasInstructions}</p>
          </CardHeader>
          <CardContent>
            <VASScale
              value={answers.eq_vas_0_100}
              onChange={handleVASChange}
              leftLabel="0 - Peor salud imaginable"
              rightLabel="100 - Mejor salud imaginable"
              showValue={true}
              required={false}
            />
          </CardContent>
        </Card>
      )}

      {/* Preview del código */}
      {previewCode && (
        <div className="bg-gray-50 rounded-lg p-4 border">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600">Código de estado de salud</p>
              <p className="text-2xl font-mono font-bold text-gray-900">{previewCode}</p>
            </div>
            <div className="text-right">
              <p className="text-sm text-gray-600">Interpretación</p>
              <p className="text-sm font-medium text-teal-700">
                {previewCode === '11111' ? 'Estado perfecto' :
                 parseInt(previewCode.split('').reduce((a, b) => a + parseInt(b), 0)) <= 10 ? 'Estado bueno' :
                 parseInt(previewCode.split('').reduce((a, b) => a + parseInt(b), 0)) <= 15 ? 'Estado moderado' :
                 'Estado con dificultades'}
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Errores */}
      {errors.length > 0 && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4">
          <p className="text-red-700 font-medium mb-2">Por favor complete todos los campos:</p>
          <ul className="list-disc list-inside text-red-600 text-sm">
            {errors.map((err, i) => (
              <li key={i}>{err}</li>
            ))}
          </ul>
        </div>
      )}

      {/* Botón guardar */}
      <div className="flex justify-end gap-3 pt-4 border-t">
        <Button
          onClick={handleSubmit}
          className="bg-teal-600 hover:bg-teal-700"
          disabled={answeredCount < dimensions.length}
        >
          <CheckCircle className="w-4 h-4 mr-2" />
          Guardar Cuestionario
        </Button>
      </div>
    </div>
  );
};

export default EQ5D5LQuestionnaire;
